import { GoalWithProgress } from '../../../../services/api.types'
import React from 'react'
import { t } from 'ttag'
import { formatThousands } from '../../../../common/helpers/translations'
import { primaryBlue, darkBlue, altGreen } from '../../../../styles/material-ui-theme-v2'
import { openActivityIndexArticle } from '../../../../common/helpers/routeHelpers'

import { ReactComponent as FormulaIcon } from '../../../../assets/v3/icons/ranking/formula-small.svg'
import { ReactComponent as FormulaWhiteIcon } from '../../../../assets/v3/icons/ranking/formula-small-white.svg'
import { ReactComponent as StepsIcon } from '../../../../assets/v3/icons/ranking/steps.svg'
import { ReactComponent as CaloriesIcon } from '../../../../assets/v3/icons/ranking/calories.svg'
import { ReactComponent as MinutesIcon } from '../../../../assets/v3/icons/ranking/minutes.svg'
import { unitsDisplayDurationCalorieLong, unitsDisplayDurationMinuteLong } from '../../../../common/helpers/units'
import { isRtl } from '../../../../i18nInit'
import makeStyles from '@material-ui/core/styles/makeStyles'

export interface IYumChallengeActivityIndexProgressProps {
  goal: GoalWithProgress
}

const useStyles = makeStyles((theme) => ({
  mainWrapper: {
    backgroundColor: darkBlue,
    padding: '10px',
    borderRadius: '8px',
    color: 'white',
    marginBottom: '16px',
  },
  mainInner: {
    display: 'flex',
    //gap: '10px',
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
    marginBottom: '32px',
    userSelect: 'none',
    fontWeight: 700,
    fontSize: '13px',
    lineHeight: '24px',
  },
  mainIcon: {
    flexShrink: 0,
    marginRight: isRtl ? 0 : '10px',
    marginLeft: isRtl ? '10px' : 0,
  },
  mainPercentage: {
    fontWeight: 700,
    fontSize: '38px',
    lineHeight: '32px',
  },
  mainUnit: {
    fontWeight: 800,
    fontSize: '28px',
    lineHeight: '32px',
  },
  mainProgressBar: {
    borderRadius: '10px',
    height: '8px',
    marginTop: '8px',
    background: 'rgba(255, 255, 255, 0.4)',
  },
  mainProgressBarInner: {
    borderRadius: '10px',
    height: '8px',
    marginTop: '8px',
    background: 'white',
    transition: 'all 0.2s ease-out',
  },
  wrapper: {
    backgroundColor: 'white',
    padding: '10px',
    borderRadius: '8px',
    marginBottom: '16px',
  },
  total: {
    fontWeight: 500,
    fontSize: '28px',
    lineHeight: '33px',
  },
  unit: {
    fontWeight: 500,
    fontSize: '18px',
    lineHeight: '21px',
    opacity: 0.5,
    marginLeft: '4px',
  },
  progressBar: {
    borderRadius: '10px',
    height: '8px',
    marginTop: '8px',
    background: 'rgba(36, 40, 78, 0.1)',
  },
  progressBarInner: {
    borderRadius: '10px',
    height: '8px',
    marginTop: '8px',
    transition: 'all 0.2s ease-out',
  },
  linkWrapper: { textAlign: 'center', marginBottom: '16px' },
  link: {
    color: primaryBlue,
    fontSize: '13px',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
      '@media (hover: none)': {
        textDecoration: 'none',
      },
    },
    '&:focus': {
      textDecoration: 'underline',
      '@media (hover: none)': {
        textDecoration: 'none',
      },
    },
    '&:active': {
      textDecoration: 'underline',
      '@media (hover: none)': {
        textDecoration: 'underline !important',
      },
    },
  },
  separator: { height: '1px', background: darkBlue, opacity: 0.25, margin: '16px 0' },
  componentInner: { display: 'flex', flexDirection: 'row', justifyContent: 'space-between' /*, gap: '8px'*/ },
  componentIcon: { flexShrink: 0 },
  componentText: {
    flexGrow: 1,
    fontSize: '13px',
    marginLeft: '8px',
    marginRight: '8px',
    textAlign: isRtl ? 'right' : 'left',
  },
  componentOpacity: { opacity: 0.5 },
  componentDivider: { margin: '0 2px' },
}))

export default function YumChallengeActivityIndexProgress(props: IYumChallengeActivityIndexProgressProps) {
  const classes = useStyles()
  const { goal } = props

  const {
    stepsTotal,
    stepsGoal,
    stepsPercentage,
    caloriesTotal,
    caloriesGoal,
    caloriesPercentage,
    intensiveMinutesTotal,
    intensiveMinutesGoal,
    intensiveMinutesPercentage,
    totalPercentage,
  } = goal || {
    stepsTotal: 0,
    stepsGoal: 0,
    stepsPercentage: 0,
    caloriesTotal: 0,
    caloriesGoal: 0,
    caloriesPercentage: 0,
    intensiveMinutesTotal: 0,
    intensiveMinutesGoal: 0,
    intensiveMinutesPercentage: 0,
    totalPercentage: 0,
  }

  const MainComponent = ({ percentage }: { percentage: number }) => (
    <div
      className={classes.mainWrapper}
      style={{
        backgroundColor: `${percentage >= 100 ? altGreen : darkBlue}`,
      }}
    >
      <div className={classes.mainInner}>
        <div className={classes.mainIcon}>{percentage < 100 ? <FormulaIcon /> : <FormulaWhiteIcon />}</div>
        {t`Activity index`}
      </div>
      <span className={classes.mainPercentage}>{percentage}</span> <span className={classes.mainUnit}>%</span>
      <div className={classes.mainProgressBar}>
        <div
          className={classes.mainProgressBarInner}
          style={{
            width: `${percentage > 100 ? '100' : percentage}%`,
          }}
        />
      </div>
    </div>
  )

  const SubComponent = ({
    Icon,
    type,
    total,
    goal,
    percentage,
  }: {
    Icon: any
    type: string
    total: number
    goal: number
    percentage: number
  }) => (
    <div>
      <div className={classes.componentInner}>
        <div className={classes.componentIcon}>
          <Icon />
        </div>
        <div className={classes.componentText}>
          <div>{type}</div>
          <div>
            {formatThousands(total)}
            <span className={classes.componentOpacity}>
              <span className={classes.componentDivider}>/</span>
              {formatThousands(goal)}
            </span>
          </div>
        </div>
        <div>
          <span className={classes.total}>{percentage}</span>
          <span className={classes.unit}>%</span>
        </div>
      </div>
      <div className={classes.progressBar}>
        <div
          className={classes.progressBarInner}
          style={{
            background: `${percentage < 100 ? darkBlue : altGreen}`,
            width: `${percentage > 100 ? 100 : percentage}%`,
          }}
        />
      </div>
    </div>
  )

  const Separator = () => <div className={classes.separator}></div>

  return caloriesTotal ? (
    <>
      {/*<MainComponent percentage={totalPercentage} />*/}
      <div className={classes.wrapper}>
        <SubComponent
          Icon={StepsIcon}
          type={t`Steps`}
          total={stepsTotal}
          goal={stepsGoal}
          percentage={stepsPercentage}
        />
        <Separator />
        <SubComponent
          Icon={CaloriesIcon}
          type={unitsDisplayDurationCalorieLong}
          total={caloriesTotal}
          goal={caloriesGoal}
          percentage={caloriesPercentage}
        />
        <Separator />
        <SubComponent
          Icon={MinutesIcon}
          type={unitsDisplayDurationMinuteLong}
          total={intensiveMinutesTotal}
          goal={intensiveMinutesGoal}
          percentage={intensiveMinutesPercentage}
        />
      </div>
      <div className={classes.linkWrapper}>
        <a className={classes.link} href={undefined} onClick={openActivityIndexArticle}>{t`What's activity index?`}</a>
      </div>
    </>
  ) : null
}
